import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Honeypot } from './Honeypot';
import { newsletterSignupSchema } from '../../shared/api-types';

// The form collects everything except sourcePage (added from the route on submit).
const formSchema = newsletterSignupSchema.omit({ sourcePage: true });
type FormValues = z.input<typeof formSchema>;
type FormOutput = z.output<typeof formSchema>;

type Variant = 'footer' | 'full';
type Status = 'idle' | 'joined' | 'unavailable' | 'rate_limited';

const styles: Record<Variant, { input: string; label: string; error: string; note: string }> = {
  // Dark footer band
  footer: {
    input:
      'bg-surface-dark border-none rounded-full px-5 py-3 w-full text-inverse placeholder:text-muted focus:ring-2 focus:ring-accent focus:outline-none',
    label: 'text-sm text-inverse-muted',
    error: 'text-xs text-accent mt-1',
    note: 'text-sm text-accent',
  },
  // Light page sections
  full: {
    input:
      'w-full bg-surface-hover border border-border rounded-lg p-3 text-default placeholder:text-faint focus:ring-2 focus:ring-primary focus:border-primary outline-none',
    label: 'text-sm text-body',
    error: 'text-xs text-error mt-1',
    note: 'text-sm text-error',
  },
};

/**
 * Newsletter signup — the site's ONE newsletter capture (PRD 9.2 system-of-record
 * rule: no Givebutter/other signup widget anywhere). Consent is an explicit,
 * unchecked, required checkbox (PRD Section 10).
 */
export function NewsletterForm({ variant = 'footer' }: { variant?: Variant }) {
  const { pathname } = useLocation();
  const [status, setStatus] = useState<Status>('idle');
  const s = styles[variant];
  const id = (f: string) => `nl-${variant}-${f}`;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues, unknown, FormOutput>({ resolver: zodResolver(formSchema) });

  const onSubmit = async (data: FormOutput) => {
    setStatus('idle');
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, sourcePage: pathname }),
      });
      if (res.ok) {
        reset();
        setStatus('joined'); // new AND duplicate signups land here
      } else setStatus(res.status === 429 ? 'rate_limited' : 'unavailable');
    } catch {
      setStatus('unavailable');
    }
  };

  if (status === 'joined') {
    return (
      <p role="status" className={variant === 'footer' ? 'text-accent font-semibold' : 'text-success font-semibold'}>
        You're on the list. Thank you!
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative space-y-3" noValidate aria-label="Newsletter signup">
      <Honeypot field={register('website')} />
      <div className={variant === 'footer' ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
        <div>
          <label htmlFor={id('name')} className="sr-only">Name</label>
          <input id={id('name')} type="text" placeholder="Your name" autoComplete="name" className={s.input} {...register('name')} />
          {errors.name && <p className={s.error}>{errors.name.message}</p>}
        </div>
        <div>
          <label htmlFor={id('email')} className="sr-only">Email</label>
          <input id={id('email')} type="email" placeholder="Your email" autoComplete="email" className={s.input} {...register('email')} />
          {errors.email && <p className={s.error}>{errors.email.message}</p>}
        </div>
        {variant === 'full' && (
          <div className="sm:col-span-2">
            <label htmlFor={id('phone')} className="sr-only">Phone (optional)</label>
            <input id={id('phone')} type="tel" placeholder="Phone (optional)" autoComplete="tel" className={s.input} {...register('phone')} />
          </div>
        )}
      </div>
      <label className={`flex items-start gap-3 cursor-pointer ${s.label}`}>
        <input type="checkbox" className="mt-0.5 w-4 h-4 accent-primary" {...register('consent')} />
        <span>Yes, send me impact stories and ways to help. Unsubscribe any time.</span>
      </label>
      {errors.consent && <p className={s.error}>{errors.consent.message}</p>}
      {status === 'unavailable' && (
        <p role="alert" className={s.note}>Signups are paused for a moment — please try again later.</p>
      )}
      {status === 'rate_limited' && (
        <p role="alert" className={s.note}>Too many attempts — please try again in a few minutes.</p>
      )}
      <button type="submit" className="btn btn-lime" disabled={isSubmitting}>
        {isSubmitting ? 'Joining…' : 'Join'}
      </button>
    </form>
  );
}
